import { DeleteOutlined, EditOutlined, LoadingOutlined } from '@ant-design/icons'
import { getOperationName } from '@apollo/client/utilities'
import { Button, Input, List, Popconfirm, Spin, Typography, message } from 'antd'
import cls from 'classnames'
import React from 'react'
import { useTranslation } from 'react-i18next'
import InfiniteScroll from 'react-infinite-scroll-component'
import { useHistory } from 'react-router-dom'

import {
  GetKnowledgeGroupsDocument,
  useGetKnowledgeGroupPostsQuery,
  useGetViewerQuery,
  useKnowledgeGroupDeleteMutation,
} from '@/generated/graphql'
import { useIsSystemAdmin } from '@/modules/admin/admin.hooks'
import { routes } from '@/routes'

import { KnowledgeCard } from './knowledge-card/knowledge-card.component'
import { KnowledgeCreateEditModal } from './knowledge-modal/knowledge-create-edit-modal.component'
import { KnowledgeGroupCreateUpdateModal } from './knowledge-modal/knowledge-group-create-edit-modal.component'
import s from './knowledge.module.less'

export const Knowledges: React.FC<{
  knowledgeGroupId: number
}> = ({ knowledgeGroupId }) => {
  const fetchCount = 16
  const { t } = useTranslation()
  const history = useHistory()
  const [search, setSerach] = React.useState<string>('')
  const { data, loading, fetchMore } = useGetKnowledgeGroupPostsQuery({
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
    variables: {
      knowledgeGroupId,
      search,
      pagination: {
        last: fetchCount,
      },
    },
    notifyOnNetworkStatusChange: true,
  })

  const [deleteGroup, { loading: deleteLoading }] = useKnowledgeGroupDeleteMutation({
    refetchQueries: [getOperationName(GetKnowledgeGroupsDocument)!],
  })

  const { data: viewerData } = useGetViewerQuery()

  const isSystemAdmin = useIsSystemAdmin()

  if (!viewerData) {
    return null
  }

  const canOperate = data?.knowledge.group.creatorUserId === viewerData.viewer.userId || isSystemAdmin

  const knowledgeGroup = data?.knowledge.group

  const knowledgePosts = data
    ? data.knowledge.group.posts.edges
        .slice()
        .reverse()
        .map(({ node }) => node)
    : []
  const pageInfo = data
    ? data.knowledge.group.posts.pageInfo
    : {
        startCursor: null,
        endCursor: null,
        hasPreviousPage: null,
        hasNextPage: null,
      }

  const parentKnowledgeGroups = data
    ? data.knowledge.group.parentGroups
      ? [...data.knowledge.group.parentGroups, data.knowledge.group]
      : [data.knowledge.group]
    : null

  const header = (
    <div className={s.header}>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <Typography.Title style={{ marginBottom: 0 }} level={4}>
          {data?.knowledge.group.name}
        </Typography.Title>
        {canOperate && (
          <>
            <KnowledgeGroupCreateUpdateModal knowledgeGroup={knowledgeGroup}>
              <EditOutlined className={s.iconAction} />
            </KnowledgeGroupCreateUpdateModal>
            <div>
              <Popconfirm
                title={t('knowledge.group.delete')}
                onConfirm={async () => {
                  const res = await deleteGroup({
                    variables: {
                      knowledgeGroupId: knowledgeGroup?.knowledgeGroupId ?? 0,
                    },
                  })
                  if (res.data && res.data.knowledgeGroupDelete) {
                    message.success(t('knowledge.group.deleteComplete'))
                  }
                  history.push(routes.knowledgebase.home())
                }}
              >
                <DeleteOutlined className={cls(s.iconAction, s.delete)} />
              </Popconfirm>
            </div>
          </>
        )}
      </div>
      <div className={s.action}>
        {!!knowledgeGroupId && (
          <KnowledgeCreateEditModal knowledgeGroupId={knowledgeGroupId}>
            <Button style={{ paddingRight: 0 }} type='link'>
              {t('knowledge.create')}
            </Button>
          </KnowledgeCreateEditModal>
        )}
        <KnowledgeGroupCreateUpdateModal parentKnowledgeGroups={parentKnowledgeGroups}>
          <Button type='link'>{knowledgeGroupId ? t('knowledge.group.createSub') : t('knowledge.group.create')}</Button>
        </KnowledgeGroupCreateUpdateModal>
        <Input.Search
          style={{ maxWidth: 200 }}
          onSearch={(value) => setSerach(value)}
          placeholder={t('knowledge.searchCurrentCatalog')}
        />
      </div>
    </div>
  )

  const loadingComponent = loading && (
    <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
      <Spin size='large' delay={300} />
    </div>
  )

  const content = (
    <>
      <InfiniteScroll
        dataLength={knowledgePosts.length}
        loader={loadingComponent}
        hasMore={!search && !!pageInfo.hasPreviousPage}
        next={() => {
          fetchMore({
            variables: {
              pagination: {
                last: fetchCount,
                before: pageInfo.startCursor,
              },
            },
          })
        }}
      >
        <List>
          {knowledgePosts.map((knowledgePost) => (
            <KnowledgeCard key={knowledgePost.knowledgePostId} knowledgePost={knowledgePost} />
          ))}
        </List>
      </InfiniteScroll>
    </>
  )

  return (
    <>
      {deleteLoading && <div className={s.deleteMask} />}
      {deleteLoading && <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} className={s.deleteSpin} />}
      <div className={s.knowledges}>
        {header}
        {content}
      </div>
    </>
  )
}
